/**
 * Hypnotised (or dragged) Pixel — and his drag kids — shove hero UI out of the way. Elements marked `data-drone-push` get knocked
 * away on contact (via the CSS `translate`/`rotate` properties, so they never fight their own
 * transform animations) and drift home once he snaps out of it.
 */

interface Body {
  x: number;
  y: number;
  vx: number;
  vy: number;
  r: number;
  vr: number;
}

const bodies = new Map<HTMLElement, Body>();
let elements: HTMLElement[] = [];
let lastScan = -10;

export function updateUiPush(
  t: number,
  dt: number,
  drones: Array<{ x: number; y: number; r: number }>,
  active: boolean,
) {
  if (t - lastScan > 1) {
    lastScan = t;
    elements = Array.from(document.querySelectorAll<HTMLElement>('[data-drone-push]'));
  }
  const step = Math.min(dt, 1 / 30);
  for (const el of elements) {
    let b = bodies.get(el);
    if (!b) {
      b = { x: 0, y: 0, vx: 0, vy: 0, r: 0, vr: 0 };
      bodies.set(el, b);
    }
    if (active && drones.length) {
      // Where the element sits right now (including any push so far)
      const rect = el.getBoundingClientRect();
      const left = rect.left;
      const top = rect.top;
      for (const drone of drones) {
        const cx = Math.max(left, Math.min(drone.x, left + rect.width));
        const cy = Math.max(top, Math.min(drone.y, top + rect.height));
        let dx = cx - drone.x;
        let dy = cy - drone.y;
        let dist = Math.hypot(dx, dy);
        if (dist < 1e-3) {
          dx = left + rect.width / 2 - drone.x;
          dy = top + rect.height / 2 - drone.y;
          dist = Math.hypot(dx, dy) || 1;
        }
        const overlap = drone.r - dist;
        if (overlap > 0) {
          const nx = dx / dist;
          const ny = dy / dist;
          b.vx += nx * overlap * 26 * step * 60 * 0.12;
          b.vy += ny * overlap * 26 * step * 60 * 0.12;
          b.vr += (nx - ny) * overlap * 0.6;
        }
      }
      // Loose while he's rampaging: heavy friction, very weak pull home
      b.vx += (-b.x * 0.8 - b.vx * 3.5) * step;
      b.vy += (-b.y * 0.8 - b.vy * 3.5) * step;
      b.vr += (-b.r * 0.8 - b.vr * 3.5) * step;
    } else {
      // Snapped out of it: everything glides home (critically damped)
      const W = 5;
      b.vx += (-b.x * W * W - 2 * W * b.vx) * step;
      b.vy += (-b.y * W * W - 2 * W * b.vy) * step;
      b.vr += (-b.r * W * W - 2 * W * b.vr) * step;
    }
    b.x += b.vx * step;
    b.y += b.vy * step;
    b.r = Math.max(-25, Math.min(25, b.r + b.vr * step));
    // Keep things on screen
    b.x = Math.max(-window.innerWidth * 0.4, Math.min(window.innerWidth * 0.4, b.x));
    b.y = Math.max(-window.innerHeight * 0.3, Math.min(window.innerHeight * 0.3, b.y));

    const still = Math.abs(b.x) < 0.05 && Math.abs(b.y) < 0.05 && Math.abs(b.r) < 0.02;
    const tr = still ? '' : `${b.x.toFixed(2)}px ${b.y.toFixed(2)}px`;
    const rot = still ? '' : `${b.r.toFixed(2)}deg`;
    if (el.style.translate !== tr) el.style.translate = tr;
    if (el.style.rotate !== rot) el.style.rotate = rot;
  }
}

export function resetUiPush() {
  bodies.forEach((_, el) => {
    el.style.translate = '';
    el.style.rotate = '';
  });
  bodies.clear();
}
